import styled from 'styled-components'
import dayjs from 'dayjs'
import { useApolloClient } from '@apollo/client'
import useAuth from '../../hooks/useAuth'
import viewDoc from '../../graphql/viewDoc'
import downloadDoc from '../../graphql/downloadDoc'
import ScreenLoader from '../ScreenLoader/ScreenLoader'
import DocLink from './DocLink/DocLink'

const Table = styled.table`
  width: 100%;
  color: var(--color-text-primary);
  font-size: 12px;
  text-align: left;
  border: 1px solid var(--color-bg-sidebar);
  border-spacing: 0;
  background-color: var(--color-bg-card);
`

const Header = styled.tr`
  background-color: var(--color-bg-sidebar);
  color: var(--color-text-primary);
`

const Head = styled.th`
  min-height: 45px;
  padding: 10px;
  text-transform: capitalize;

  &.hide-mobile {
    @media only screen and (max-width: 610px) {
      display: none;
    }
  }
`

const Doc = styled.tr`
  transition: background-color 0.2s ease;

  &:nth-child(even) {
    background-color: rgba(42, 42, 42, 0.5);
  }

  &:hover {
    background-color: var(--color-bg-sidebar);
  }
`

const Col = styled.td`
  height: 45px;
  padding: 10px;
  border-bottom: 1px solid var(--color-bg-sidebar);

  &:last-child {
    width: 100px;
  }

  &.hide-mobile {
    @media only screen and (max-width: 610px) {
      display: none;
    }
  }
`

const Links = styled.div`
  display: grid;
  grid-gap: 5px;
  grid-template-columns: repeat(3, max-content);

  @media only screen and (max-width: 610px) {
    grid-template-columns: max-content;
    justify-items: center;
  }
`

const Fallback = styled.p`
  font-size: 14px;
  color: #a5acc2;
  text-align: center;
  margin-top: 22px;
`

const ErrorMsg = styled.p`
  color: red;
`

const DOC_DATE = 'MMMM D, YYYY'

const DocsTable = ({ docs = [], loading, error }) => {
  const { selectedOrg } = useAuth()
  const client = useApolloClient()


  const queryOperation = (operation, fileId) => {
    let query
    if (operation === 'view') query = viewDoc
    if (operation === 'download') query = downloadDoc

    return client.query({
      query,
      variables: {
        organizationId: selectedOrg.value,
        fileId,
      },
      context: {
        version: 2,
      },
    })
  }

  const handleView = async fileId => {
    try {
      const { data } = await queryOperation('view', fileId)
      window.open(data.viewDocument.url)
    } catch (err) {
      throw err
    }
  }

  const handleDownload = async fileId => {
    try {
      const { data } = await queryOperation('download', fileId)
      window.open(data.downloadDocument.url, '_self')
    } catch (err) {
      throw err
    }
  }

  if (loading) return <ScreenLoader />
  if (error) return <ErrorMsg>{error.message}</ErrorMsg>

  return (
    <>
      <Table>
        <thead>
          <Header>
            <Head>Name</Head>
            <Head className='hide-mobile'>Email</Head>
            <Head>Published On</Head>
            <Head>Type</Head>
            <Head />
          </Header>
        </thead>
        <tbody>
          {docs.map(doc => {
            return (
              <Doc key={doc.id}>
                <Col>{doc.name}</Col>
                <Col className='hide-mobile'>{doc.publishedBy ? doc.publishedBy.id : ''}</Col>
                <Col>{dayjs(doc.publishedDate).format(DOC_DATE)}</Col>
                <Col>
                  {doc.type
                    ? doc.type.split('_').map(capitalize).join(' ')
                    : ''}
                </Col>
                <Col>
                  <Links>
                    <DocLink className='documents-view-button' handler={() => handleView(doc.id)}>View</DocLink>
                    <DocLink className='documents-download-button' handler={() => handleDownload(doc.id)}>
                      Download
                    </DocLink>
                  </Links>
                </Col>
              </Doc>
            )
          })}
        </tbody>
      </Table>
      {docs.length < 1 && <Fallback>No documents found!</Fallback>}
    </>
  )
}

function capitalize(str) {
  if (!str) return
  return str[0].toUpperCase() + str.slice(1).toLowerCase()
}

export default DocsTable
